import React, { ChangeEvent, useMemo, useState } from "react";
import { Checkbox, FormControlLabel, FormGroup, IconButton, InputAdornment, Paper, Stack, Switch, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from '@mui/icons-material/Clear';

import MatchFieldsButton, { MatchFieldValue } from "./MatchFieldsButton";
import SortButton from "routes/tagManagement/SortButton";

export type SelectionMode = "single" | "multi";

export interface QuerySearchBarSettings {
  includeShared: boolean;
  matchField: MatchFieldValue;
  searchValue: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
}

interface QuerySearchBarProps {
  onChangeSearchSettings(settings: QuerySearchBarSettings): void;
  onChangeSelectMultiple?(value: boolean): void;
  selectMultiple?: boolean;
  settings: QuerySearchBarSettings;
  showSelectMultipleToggle?: boolean;
}

export const defaultQuerySearchBarSettings: QuerySearchBarSettings = {
  includeShared: false,
  matchField: "any",
  searchValue: "",
  sortBy: "updated_at",
  sortOrder: "desc"
};

export default function QuerySearchBar(props: QuerySearchBarProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const [_selectMultiple, setSelectMultiple] = useState<boolean>(false);

  const sortByOptions = useMemo(() => {
    return [{
      label: i18n.t("query-search-bar.sort-by-options.created"),
      value: "created_at"
    }, {
      label: i18n.t("query-search-bar.sort-by-options.updated"),
      value: "updated_at"
    }, {
      label: i18n.t("query-search-bar.sort-by-options.name"),
      value: "name"
    }]
  }, []);

  const selectMultiple = props.selectMultiple || _selectMultiple;

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1
      }}>
      <TextField
        autoComplete="off"
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const newSettings = {
            ...props.settings,
            searchValue: event.target.value
          };
          props.onChangeSearchSettings(newSettings);
        }}
        placeholder={i18n.t("query-search-bar.search-placeholder")}
        size="small"
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                {props.settings.searchValue &&
                  <IconButton
                    color="error"
                    onClick={() => {
                      const newSettings = {...props.settings, searchValue: ""}
                      props.onChangeSearchSettings(newSettings);
                    }}
                    size="small"
                  >
                    <ClearIcon/>
                  </IconButton>
                }
              </InputAdornment>
            ),
            startAdornment: <InputAdornment position="start"><SearchIcon/></InputAdornment>
          }
        }}
        sx={{flex: 1, minWidth: 200}}
        value={props.settings.searchValue}
      />
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 1
        }}>
        <FormGroup>
          <FormControlLabel 
            control={<Checkbox 
              onChange={(event) => {
                const newSettings = {
                  ...props.settings,
                  includeShared: event.target.checked
                };
                props.onChangeSearchSettings(newSettings);
              }}
              size="small" />
            } 
            label={i18n.t("query-search-bar.include-shared-label")}
            sx={{ marginRight: 0 }} />
        </FormGroup>
        <MatchFieldsButton 
          onChange={(field) => {
            const newSettings = {
              ...props.settings,
              matchField: field
            };
            props.onChangeSearchSettings(newSettings);
          }}
          selectedField={props.settings.matchField}
        />
      </Stack>
      <SortButton
        initialOrder="desc"
        initialSort={props.settings.sortBy}
        onChange={(sortBy, order) => {
          const newSettings = {
            ...props.settings,
            sortBy: sortBy,
            sortOrder: order
          };
          props.onChangeSearchSettings(newSettings);
        }}
        sortByOptions={sortByOptions}
      />
      {props.showSelectMultipleToggle &&
        <FormControlLabel
          control={
            <Switch
              checked={selectMultiple}
              onChange={(event, checked) => {
                if (!props.selectMultiple) {
                  setSelectMultiple(checked);
                }
                if (props.onChangeSelectMultiple) {
                  props.onChangeSelectMultiple(checked);
                }
              }}
            />
          }
          label={i18n.t("query-search-bar.select-multiple")}
        />
      }
    </Stack>
  );
}